import { useId, useState, type FormEvent, type ReactNode } from "react";

import { useAuth } from "../auth/AuthContext.js";
import { Logo } from "../components/Logo.js";
import { ApiError } from "../lib/api.js";

export function Login(): ReactNode {
  const { login } = useAuth();
  const addressId = useId();
  const addressHintId = useId();
  const passwordId = useId();

  const [address, setAddress] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Standaard aan: de webmail wordt vooral als app op een telefoon gebruikt,
  // en daar is elke keer opnieuw inloggen de grootste ergernis. Wie een
  // gedeelde computer gebruikt, vinkt het uit — de tekst zegt dat expliciet.
  const [remember, setRemember] = useState(true);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);
    try {
      await login(address, password, remember);
    } catch (cause) {
      // De server houdt zijn meldingen bewust vaag (geen onderscheid tussen een
      // onbekend adres en een fout wachtwoord); die tekst nemen we letterlijk over.
      setError(
        cause instanceof ApiError ? cause.message : "Er ging iets mis. Probeer het opnieuw.",
      );
      // Het wachtwoord blijft niet langer dan nodig in het geheugen van de
      // component staan; het adres laten we staan zodat opnieuw proberen vlot gaat.
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <form className="login-card" onSubmit={onSubmit} noValidate>
        <div className="login-brand">
          <Logo height={48} />
        </div>

        <h1 className="login-title">Welkom bij uw webmail</h1>
        {/* De webmail bedient meerdere domeinen; welke, weet de server. Daarom
            nergens een domeinlijst, maar wel de vraag om het volledige adres. */}
        <p className="login-intro">
          Log in met uw volledige e-mailadres en het bijbehorende wachtwoord.
        </p>

        {error !== null && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="field">
          <label className="field-label" htmlFor={addressId}>
            Volledig e-mailadres
          </label>
          <input
            id={addressId}
            className="field-input"
            type="email"
            name="username"
            placeholder="naam@uwdomein.nl"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            required
            disabled={busy}
            aria-describedby={addressHintId}
            value={address}
            onChange={(event) => setAddress(event.target.value)}
          />
          <p className="field-hint" id={addressHintId}>
            Inclusief het deel achter de @ — alleen uw gebruikersnaam werkt niet.
          </p>
        </div>

        <div className="field">
          <label className="field-label" htmlFor={passwordId}>
            Wachtwoord
          </label>
          <input
            id={passwordId}
            className="field-input"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            disabled={busy}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        <label className="login-remember">
          <input
            type="checkbox"
            checked={remember}
            disabled={busy}
            onChange={(event) => setRemember(event.target.checked)}
          />
          <span>
            Aangemeld blijven op dit apparaat
            <small>Niet aanvinken op een gedeelde of openbare computer.</small>
          </span>
        </label>

        <button type="submit" className="button button--primary button--block" disabled={busy}>
          {busy ? "Bezig met inloggen…" : "Inloggen"}
        </button>

        <p className="login-foot">
          Webmail voor de e-maildomeinen die RULIDO beheert. Uw wachtwoord is hetzelfde als in uw
          mailprogramma op telefoon of computer.
        </p>
      </form>
    </main>
  );
}
